'use client';

/**
 * Process-wide cache for `/explorer/listing_alert/`.
 *
 * The endpoint is read by several components that commonly mount
 * together — on the home page alone `PersonalizePill` and the
 * always-rendered `RenterProfileWizardModal` each call
 * `useListingAlert`, and `ListingAlertAutoEnable` needs the same row.
 * Before this module every instance issued its own GET, and the
 * login transition re-fired all of them, so a single page load could
 * hit the endpoint five or six times.
 *
 * Two jobs:
 *
 *  1. **Dedupe.** Concurrent callers share one in-flight request, and
 *     later callers reuse the cached row instead of refetching.
 *  2. **Coherence.** All consumers observe the same row. Previously
 *     each hook instance held private state, so a write in one (say
 *     the wizard finishing) left the others stale until they
 *     remounted — which is why call sites needed manual `refetch()`.
 *
 * Deliberately not a React context: `ListingAlertAutoEnable` and the
 * hook sit in unrelated parts of the tree, and a provider high enough
 * to cover both would re-render the whole app on every write.
 */

import { getListingAlert, type ListingAlertResponse } from '@repo/api';

let cache: ListingAlertResponse | null = null;
let inFlight: Promise<ListingAlertResponse> | null = null;
const subscribers = new Set<() => void>();

function notify() {
  subscribers.forEach(fn => fn());
}

/** Current cached row, or null if nothing has loaded yet. */
export function getListingAlertCache(): ListingAlertResponse | null {
  return cache;
}

/**
 * Overwrite the cache and wake every consumer. Call after any
 * successful write so other mounted components don't keep rendering
 * a pre-write row.
 */
export function setListingAlertCache(next: ListingAlertResponse | null): void {
  cache = next;
  notify();
}

/**
 * Drop everything — used on logout so the next session (possibly a
 * different user on the same browser) can't read stale data.
 */
export function clearListingAlertCache(): void {
  cache = null;
  inFlight = null;
  notify();
}

/**
 * Fetch the row, reusing the cache and coalescing concurrent calls.
 *
 * @param force bypass the cache but still share an in-flight request
 */
export function loadListingAlert(force = false): Promise<ListingAlertResponse> {
  if (!force && cache) return Promise.resolve(cache);
  // A refetch racing an initial load should still ride along rather
  // than opening a second connection.
  if (inFlight) return inFlight;

  inFlight = getListingAlert()
    .then(data => {
      cache = data;
      inFlight = null;
      notify();
      return data;
    })
    .catch(error => {
      // Clear the latch so a later attempt can retry; leave `cache`
      // alone so a transient failure doesn't blow away good data.
      inFlight = null;
      throw error;
    });

  return inFlight;
}

/** Subscribe to cache changes. Returns an unsubscribe function. */
export function subscribeToListingAlert(fn: () => void): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}
